import React from 'react';
import { useForm, FormProvider, useFormContext, useController } from 'react-hook-form';
import { Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';

// Vervangt availity-reactstrap-validation (AvForm/AvField) met react-hook-form + reactstrap 9.
// De wrappers zijn function-componenten (hooks); de bestaande class-componenten kunnen ze
// gewoon renderen. onValidSubmit krijgt de waarden-object (net als AvForm's tweede argument).

// `values` (reactief) vervangt AvForm's `model`-prop: verandert `values`, dan synct het
// formulier automatisch mee (bijv. na "herstellen naar standaard").
export function ValidatedForm({ onValidSubmit, defaultValues, values, children, ...rest }) {
  const methods = useForm({ defaultValues, values, mode: 'onBlur' });
  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onValidSubmit)} {...rest}>
        {children}
      </Form>
    </FormProvider>
  );
}

/**
 * De knoppenrij onder een formulier in een venster.
 *
 * Bij toevoegen blijft het venster staan: opslaan zet de nieuwe regel in de tabel
 * erachter en legt meteen een leeg formulier klaar. Zo tik je er tien achter elkaar in
 * zonder telkens opnieuw het venster te openen, en zie je ondertussen de lijst groeien.
 * Sluiten is dan de manier om te stoppen.
 *
 * Bij aanpassen valt er niets te herhalen — daar slaat één knop op en is het klaar.
 */
export function FormulierKnoppen({ snel, onSluiten, opslaan = 'Opslaan', volgende = 'Opslaan en volgende' }) {
  if (!snel) {
    return <div className="actierij"><button type="submit" className="btn btn-primary">{opslaan}</button></div>;
  }

  return (
    <div className="actierij">
      <button type="submit" className="btn btn-primary">{volgende}</button>
      <button type="button" className="btn btn-secondary" onClick={onSluiten}>Sluiten</button>
    </div>
  );
}

// Eén tekst/wachtwoord/e-mail/number-veld met label, validatie en foutmelding.
// `rules` volgt react-hook-form (required/min/max/pattern/validate).
export function TextField({ name, label, type = 'text', required, placeholder, rules = {}, bewerk, ...rest }) {
  const { register, formState: { errors } } = useFormContext();
  const finalRules = { ...rules };
  if (required && finalRules.required === undefined) {
    finalRules.required = 'Dit veld is verplicht';
  }
  const error = errors[name];
  // reactstrap's Input geeft de standaard `ref` niet door aan de DOM-input; die moet via
  // `innerRef`. Zonder dit registreert react-hook-form de waarde niet (velden lijken leeg).
  const { ref, onChange, ...field } = register(name, finalRules);

  // `bewerk` schrijft de invoer om terwijl je typt (bijvoorbeeld naar kleine letters).
  // Dat is prettiger dan afkeuren achteraf: het veld doet gewoon wat het zegt.
  const bijWijziging = bewerk
    ? (gebeurtenis) => {
        const bewerkt = bewerk(gebeurtenis.target.value);

        if (bewerkt !== gebeurtenis.target.value) {
          gebeurtenis.target.value = bewerkt;
        }

        return onChange(gebeurtenis);
      }
    : onChange;

  return (
    <FormGroup>
      {label && <Label for={name}>{label}</Label>}
      <Input
        id={name}
        type={type}
        placeholder={placeholder}
        invalid={!!error}
        innerRef={ref}
        onChange={bijWijziging}
        {...field}
        {...rest}
      />
      {error && <FormFeedback>{error.message}</FormFeedback>}
    </FormGroup>
  );
}

/**
 * Een getal met een min- en een plusknop.
 *
 * Voor de waarden waar het om kleine gehele getallen gaat — rondes, spelers per
 * tafel, tafels in een kroeg. Daar is typen omslachtiger dan tikken, zeker op een
 * telefoon, en het scheelt de hele klasse invoerfouten waar een tekstveld op
 * gecontroleerd moet worden: letters, min-tekens, lege waarden.
 *
 * De invoer blijft gewoon een getalveld, dus met het toetsenbord kan het ook.
 */
export function GetalVeld({ name, label, min = 0, max = 999, stap = 1, hulp, rules = {}, ...rest }) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control, rules });

  const getal = Number.parseInt(field.value, 10);
  const waarde = Number.isNaN(getal) ? min : getal;

  const zet = (nieuw) => field.onChange(String(Math.min(max, Math.max(min, nieuw))));

  return (
    <FormGroup>
      {label && <Label for={name} className="d-block">{label}</Label>}
      <div className="teller teller-veld">
        <button type="button" aria-label={`${label || name}: eentje minder`}
                disabled={waarde <= min} onClick={() => zet(waarde - stap)}>−</button>
        <input id={name}
               type="number"
               inputMode="numeric"
               min={min}
               max={max}
               value={field.value === '' || field.value === undefined ? '' : field.value}
               onChange={(gebeurtenis) => field.onChange(gebeurtenis.target.value)}
               onBlur={() => { field.onBlur(); zet(waarde); }}
               aria-label={label || name}
               {...rest} />
        <button type="button" aria-label={`${label || name}: eentje meer`}
                disabled={waarde >= max} onClick={() => zet(waarde + stap)}>+</button>
      </div>
      {hulp && <p className="stille-tekst">{hulp}</p>}
      {fieldState.error && <div className="invalid-feedback d-block">{fieldState.error.message}</div>}
    </FormGroup>
  );
}

/**
 * Aan of uit als twee zichtbare keuzes.
 *
 * Een vinkje laat alleen zien wat er aan staat; hier staan allebei de standen er, met
 * de actieve opgelicht. Dat leest sneller en is met een duim beter te raken.
 */
export function Schakelaar({ waarde, opWijziging, aan = 'Aan', uit = 'Uit', label }) {
  return (
    <div className="segment" role="group" aria-label={label}>
      <button type="button" aria-pressed={!!waarde} onClick={() => opWijziging(true)}>{aan}</button>
      <button type="button" aria-pressed={!waarde} onClick={() => opWijziging(false)}>{uit}</button>
    </div>
  );
}

/** Dezelfde schakelaar, maar als veld in een formulier. */
export function SchakelVeld({ name, label, aan, uit, hulp }) {
  const { control } = useFormContext();
  const { field } = useController({ name, control });

  return (
    <FormGroup>
      {label && <Label>{label}</Label>}
      <div>
        <Schakelaar label={label} aan={aan} uit={uit}
                    waarde={field.value === true || field.value === 'true'}
                    opWijziging={field.onChange} />
      </div>
      {hulp && <p className="stille-tekst">{hulp}</p>}
    </FormGroup>
  );
}

// Select-veld (vervangt AvField type="select"). Opties als children (<option>).
// Een eigen `onChange` wordt naast die van react-hook-form aangeroepen.
export function SelectField({ name, label, required, rules = {}, children, onChange, ...rest }) {
  const { register, formState: { errors } } = useFormContext();
  const finalRules = { ...rules };
  if (required && finalRules.required === undefined) {
    finalRules.required = 'Dit veld is verplicht';
  }
  const error = errors[name];
  const { ref, onChange: rhfOnChange, ...field } = register(name, finalRules);
  const handleChange = (e) => { rhfOnChange(e); if (onChange) onChange(e); };
  return (
    <FormGroup>
      {label && <Label for={name}>{label}</Label>}
      <Input id={name} type="select" invalid={!!error} innerRef={ref} onChange={handleChange} {...field} {...rest}>
        {children}
      </Input>
      {error && <FormFeedback>{error.message}</FormFeedback>}
    </FormGroup>
  );
}
